import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { myApi } from '../../config/axiosConfig';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';

// Iconos
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PreviewIcon from '@mui/icons-material/Preview';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const temaFsc = themeQuartz.withParams({
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
    foregroundColor: '#00152b',
    backgroundColor: '#ffffff',
    headerBackgroundColor: '#f8f8fa',
    headerTextColor: '#00152b',
    headerFontWeight: 600,
    borderColor: '#d9d9df',
    rowBorder: true,
    rowHoverColor: '#eef4ff',
    wrapperBorderRadius: 6
});

const formatNumber = (value, decimals = 5) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const number = Number(value);

    if (!Number.isFinite(number)) {
        return '';
    }

    return number.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

const FscPrueba = () => {

    const [archivo, setArchivo] = useState(null);
    const [data, setData] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [actualizando, setActualizando] = useState(false);

    const columnDefs = useMemo(() => [
        {
            field: 'fila_excel',
            headerName: 'FILA EXCEL',
            width: 115,
            type: 'numericColumn'
        },
        {
            field: 'start_price',
            headerName: 'DOE FUEL',
            width: 135,
            type: 'numericColumn',
            valueFormatter: (params) => formatNumber(params.value, 3)
        },
        {
            field: 'amount_actual',
            headerName: 'AMOUNT ACTUAL',
            width: 165,
            type: 'numericColumn',
            valueFormatter: (params) => formatNumber(params.value, 5)
        },
        {
            field: 'amount_nuevo',
            headerName: 'AMOUNT EXCEL',
            width: 165,
            type: 'numericColumn',
            valueFormatter: (params) => formatNumber(params.value, 5)
        },
        {
            field: 'cambio',
            headerName: 'CAMBIO',
            width: 120,
            cellRenderer: (params) => (
                <Chip
                    size="small"
                    label={params.value ? 'SÍ' : 'NO'}
                    color={params.value ? 'warning' : 'success'}
                />
            )
        },
        {
            field: 'estatus',
            headerName: 'ESTATUS',
            width: 130,
            cellRenderer: (params) => (
                <Chip
                    size="small"
                    label={params.value || 'ERROR'}
                    color={params.value === 'LISTO' ? 'success' : 'error'}
                />
            )
        },
        {
            field: 'mensaje',
            headerName: 'DETALLE',
            flex: 1,
            minWidth: 220
        }
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true
    }), []);

    const handleArchivo = (event) => {
        const nuevoArchivo = event.target.files?.[0] || null;

        setArchivo(nuevoArchivo);
        setData([]);
        setResumen(null);
    };

    const handleGenerarPreview = async () => {

        if (!archivo) {
            Swal.fire({
                icon: 'warning',
                title: 'Selecciona un archivo Excel'
            });
            return;
        }

        try {
            setCargando(true);

            const formData = new FormData();
            formData.append('archivo', archivo);

            const response = await myApi.post(
                '/mcleod/fsc/PRUEBA/preview',
                formData
            );

            if (!response.data.success) {
                throw new Error(response.data.message || 'No fue posible leer el archivo');
            }

            setData(response.data.data || []);
            setResumen(response.data.resumen || null);

            Swal.fire({
                icon: 'success',
                title: 'Previsualización generada',
                text: 'No se actualizó ningún dato en McLeod.'
            });

        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: 'error',
                title: 'Error al generar la previsualización',
                text: error.response?.data?.message || error.message
            });

        } finally {
            setCargando(false);
        }
    };

    const filasConCambio = data.filter(
        (fila) => fila.estatus === 'LISTO' && fila.cambio === true
    );

    const handleActualizar = async () => {

        if (filasConCambio.length === 0) {
            Swal.fire({
                icon: 'info',
                title: 'No hay cambios pendientes',
                text: 'Todos los registros ya tienen el mismo amount.'
            });
            return;
        }

        const confirmacion = await Swal.fire({
            title: '¿Actualizar McLeod?',
            html: `
                Se actualizarán <b>${filasConCambio.length}</b> registros de la tabla
                <b>PRUEBA</b>.<br/><br/>
                Solo se cambiará el campo <b>amount</b>.
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, actualizar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#6c757d'
        });

        if (!confirmacion.isConfirmed) {
            return;
        }

        try {
            setActualizando(true);

            const response = await myApi.put('/mcleod/fsc/PRUEBA/update', {
                usuario: Cookies.get('ebt_nombre') || 'SIN USUARIO',
                filas: filasConCambio.map((fila) => ({
                    id: fila.id,
                    start_price: fila.start_price,
                    amount_nuevo: fila.amount_nuevo
                }))
            });

            const { resumen: resumenActualizacion, resultados } = response.data;

            const resultadosPorId = new Map(
                (resultados || []).map((resultado) => [
                    String(resultado.id),
                    resultado
                ])
            );

            setData((anterior) =>
                anterior.map((fila) => {
                    const resultado = resultadosPorId.get(String(fila.id));

                    if (!resultado) {
                        return fila;
                    }

                    if (resultado.success && !resultado.omitido) {
                        return {
                            ...fila,
                            amount_actual: resultado.amount_nuevo,
                            cambio: false,
                            mensaje: 'Actualizado en McLeod'
                        };
                    }

                    if (!resultado.success) {
                        return {
                            ...fila,
                            estatus: 'ERROR',
                            mensaje: resultado.mensaje || 'No fue posible actualizar'
                        };
                    }

                    return fila;
                })
            );

            setResumen((anterior) => ({
                ...anterior,
                con_cambio: Math.max(
                    0,
                    (anterior?.con_cambio || 0) -
                    (resumenActualizacion?.actualizados || 0) -
                    (resumenActualizacion?.omitidos || 0)
                ),
                errores: (anterior?.errores || 0) +
                    (resumenActualizacion?.errores || 0)
            }));

            await Swal.fire({
                icon: resumenActualizacion.errores > 0 ? 'warning' : 'success',
                title: 'Proceso terminado',
                html: `
                    Actualizados: <b>${resumenActualizacion.actualizados}</b><br/>
                    Sin cambio: <b>${resumenActualizacion.omitidos}</b><br/>
                    Errores: <b>${resumenActualizacion.errores}</b>
                `
            });

        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: 'error',
                title: 'No fue posible actualizar McLeod',
                text: error.response?.data?.message || error.message
            });

        } finally {
            setActualizando(false);
        }
    };

    return (
        <Paper sx={{ padding: 2 }}>
            <Grid container spacing={2}>

                <Grid size={12}>
                    <Typography variant="h4" fontWeight="bold">
                        Actualización masiva FSC · PRUEBA
                    </Typography>

                    <Typography color="text.secondary">
                        Sube el Excel para revisar los cambios antes de actualizar McLeod.
                    </Typography>
                </Grid>

                <Grid size={12}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                        >
                            Seleccionar Excel
                            <input
                                hidden
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleArchivo}
                            />
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={
                                cargando
                                    ? <CircularProgress size={18} color="inherit" />
                                    : <PreviewIcon />
                            }
                            disabled={!archivo || cargando}
                            onClick={handleGenerarPreview}
                        >
                            {cargando ? 'Procesando...' : 'Generar previsualización'}
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            startIcon={
                                actualizando
                                    ? <CircularProgress size={18} color="inherit" />
                                    : <SystemUpdateAltIcon />
                            }
                            disabled={filasConCambio.length === 0 || actualizando || cargando}
                            onClick={handleActualizar}
                        >
                            {actualizando
                                ? 'Actualizando McLeod...'
                                : `Actualizar ${filasConCambio.length} registros`}
                        </Button>
                    </Stack>

                    {archivo && (
                        <Typography mt={1} variant="body2">
                            Archivo seleccionado: <strong>{archivo.name}</strong>
                        </Typography>
                    )}
                </Grid>

                {resumen && (
                    <Grid size={12}>
                        <Alert severity={resumen.errores > 0 ? 'warning' : 'success'}>
                            Leídos: <strong>{resumen.leidos}</strong>
                            {' · '}Listos: <strong>{resumen.listos}</strong>
                            {' · '}Con cambio: <strong>{resumen.con_cambio}</strong>
                            {' · '}Errores: <strong>{resumen.errores}</strong>
                        </Alert>
                    </Grid>
                )}

                <Grid size={12}>
                    <Box sx={{ height: 560, width: '100%' }}>
                        <AgGridReact
                            modules={[AllCommunityModule]}
                            theme={temaFsc}
                            rowData={data}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            getRowId={(params) => params.data.id}
                            rowHeight={42}
                            animateRows
                            pagination
                            paginationPageSize={100}
                            paginationPageSizeSelector={[25, 50, 100, 250]}
                            overlayNoRowsTemplate="Aún no hay una previsualización para mostrar."
                        />
                    </Box>
                </Grid>

            </Grid>
        </Paper>
    );
};

export default FscPrueba;